import {
  Button,
  Heading,
  HStack,
  PinInput,
  PinInputField,
  Text,
  Center,
  Box,
  CircularProgress,
  Flex,
  Skeleton,
  FormControl,
  FormErrorMessage,
} from '@chakra-ui/react';
import { Field, Form, Formik } from 'formik';
import { React, useState, useEffect, useCallback, useRef } from 'react';
import NoUserHeader from '../AreaLivre/NoUserHeader';
import { useContext } from 'react';
import { AccountContext } from '../AccountContext';
import { useNavigate, useLocation } from 'react-router';
import * as Yup from 'yup';
import { enviarCodigoConfirmacao as enviarSMS } from 'api/sms';
import { enviarCodigoConfirmacao as enviarWhatsApp } from 'api/whatsapp';
import { ReactComponent as SadCircle } from 'images/sad-circle.svg';
import { api } from 'api/api';

const ENDPOINT =
  process.env.REACT_APP_ENVIRONMENT === 'production'
    ? process.env.REACT_APP_BASE_URL_API_SERVER_PROD
    : process.env.REACT_APP_BASE_URL_API_SERVER_TEST;

function BotaoEnviarWhatsApp({ sendWhatsApp, successWhatsApp, successSMS, sendSMS, setSuccessSMS, setSuccessWhatsApp }) {
  const interval = useRef();
  const countdown = 10; // 10 segundos
  const [counter, setCounter] = useState(countdown);
  useEffect(() => {
    if (counter > 0) {
      interval.current = setInterval(() => {
        setCounter((prevCounter) => prevCounter - 1);
      }, 1000);
      return () => clearInterval(interval.current);
    }
  }, [counter]);
  return (
    <>
      <Button
        mt={4}
        isFullWidth
        disabled={counter}
        onClick={() => {
          console.log(counter);
          sendWhatsApp();
          setCounter(60);
        }}
      >
        {successWhatsApp ? 'Enviar novo código' : 'Enviar código via WhatsApp'}
      </Button>

      {successWhatsApp && (
        <>
          <Button
            mt={4}
            isFullWidth
            colorScheme={'teal'}
            disabled={counter}
            onClick={() => {
              setSuccessSMS(true);
              setSuccessWhatsApp(false);
              sendSMS();
              setCounter(60);
            }}
          >
            Enviar código via SMS
          </Button>
        </>
      )}

      {successWhatsApp && !!counter ? (
        <Flex alignItems={'center'} justifyContent={'space-between'} mt={4} gap={4}>
          <Text fontSize={10}>Aguarde {`${counter} segundos`}, para enviar um novo código</Text>
          <CircularProgress value={((countdown - counter) / countdown) * 100} size={4} thickness={20} color="teal" />
        </Flex>
      ) : (
        successSMS &&
        !!counter && (
          <Flex alignItems={'center'} justifyContent={'space-between'} mt={4} gap={4}>
            <Text fontSize={10}>
              Caso o SMS não seja entregue em até {counter > 60 ? `${Math.round(counter / 60)} min` : `${counter} segundos`}, você poderá enviar a
              mensagem via WhatsApp
            </Text>
            <CircularProgress value={((countdown - counter) / countdown) * 100} size={4} thickness={20} color="teal" />
          </Flex>
        )
      )}
    </>
  );
}

function GeraCodigo() {
  const { state } = useLocation();
  const { setPage } = useContext(AccountContext);

  const [successSMS, setSuccessSMS] = useState(false);
  const [successWhatsApp, setSuccessWhatsApp] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const navigate = useNavigate();
  const firstName = state.nome.split(' ').at(0);

  const sendSMS = useCallback(async () => {
    setError(null);
    setLoading(true);
    let data = null;
    try {
      data = await enviarSMS(state.cpf, state.fone);
      setSuccessSMS(!!data.success);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
    return data;
  }, [state.cpf, state.fone]);

  const sendWhatsApp = useCallback(async () => {
    setError(null);
    setLoading(true);
    let data = null;
    try {
      data = await enviarWhatsApp(state.cpf, state.fone, state.nome);
      setSuccessWhatsApp(data?.response?.status === 'success');
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
    return data;
  }, [state.cpf, state.fone, state.nome]);

  const sendCode = useCallback(async () => {
    const resultSMS = await sendSMS();

    if (!resultSMS?.success) {
      const resultWhatsApp = await sendWhatsApp();
      if (resultWhatsApp?.response?.status !== 'success') {
        setError('Erro ao enviar código de confirmação. Por favor, tente novamente mais tarde, ou contate o suporte.');
      }
    }
  }, [sendSMS, sendWhatsApp]);

  const schema = Yup.object({
    pin: Yup.string().required('Digite o código de confirmação').length(8, 'O código deve ter exatamente 8 dígitos'),
  });

  useEffect(() => {
    sendCode();
  }, [sendCode]);

  useEffect(() => {
    setPage('Confirmação de Telefone');
  }, [setPage]);

  useEffect(() => {
    setSuccessWhatsApp(successWhatsApp)
    setSuccessSMS(successSMS)
  }, [successSMS, successWhatsApp])

  if (loading) {
    return (
      <>
        <NoUserHeader />
        <Box p={8}>
          <Flex flexDirection={'column'} gap={8}>
            <Skeleton rounded={'lg'}>
              <Heading fontSize="1em">
                {`Olá ${firstName}, enviamos ${
                  successWhatsApp ? 'uma mensagem no WhatsApp' : successSMS ? 'um SMS' : null
                } com seu código de segurança.`}
                <Text fontSize={'xs'} mt={4}>
                  Insira-o abaixo e em seguida clique "Confirmar"
                </Text>
              </Heading>
            </Skeleton>

            <HStack spacing={2}>
              <PinInput as={Skeleton} isDisabled size="md">
                <PinInputField />
                <PinInputField />
                <PinInputField />
                <PinInputField />
                <PinInputField />
                <PinInputField />
                <PinInputField />
                <PinInputField />
              </PinInput>
            </HStack>
            <Button as={Skeleton} isFullWidth disabled isLoading>
              Confirmar
            </Button>
          </Flex>
          <Button as={Skeleton} mt={4} isFullWidth disabled isLoading>
            {successWhatsApp ? 'Enviar novo código' : 'Enviar código via WhatsApp'}
          </Button>

          <Flex alignItems={'center'} justifyContent={'space-between'} mt={4} gap={4}>
            <Text as={Skeleton} fontSize={10} rounded={'lg'}>
              Caso o SMS não seja entregue em até 60 segundos, você poderá enviar a mensagem via WhatsApp
            </Text>
            <CircularProgress value={0} size={4} thickness={20} />
          </Flex>
        </Box>
      </>
    );
  }

  if (error) {
    return (
      <>
        <NoUserHeader />
        <Box p={8}>
          <Center flexDirection={'column'} gap={8}>
            <SadCircle />
            <Text fontSize="xl" textAlign={'center'} fontWeight="bold">
              Não foi possível enviar o código de confirmação.
            </Text>
            <Text textAlign={'center'}>Por favor, tente novamente mais tarde, ou contate o suporte.</Text>

            <Button
              onClick={() => {
                sendCode();
              }}
            >
              Tentar novamente
            </Button>
          </Center>
        </Box>
      </>
    );
  }

  return (
    <>
      {/* <Modal isOpen={disclosure.isOpen} size="xs" onClose={disclosure.onClose} isCentered>
        <ModalOverlay />
        <ModalContent borderRadius={20} p={4} pt={8} mx={4}>
          <ModalBody>
            <Center flexDirection={'column'} gap={8}>
              <CheckCircleIcon color="green" boxSize={20} />
              <Text size="lg" textAlign={'center'}>
                Um novo código foi enviado para o número cadastrado
              </Text>
            </Center>
          </ModalBody>

          <ModalFooter>
            <Button
              isFullWidth
              colorScheme="teal"
              variant="solid"
              size="lg"
              onClick={() => {
                disclosure.onClose();
              }}
            >
              Ok
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal> */}

      <NoUserHeader />

      <Box p={8}>
        <Formik
          validateOnBlur
          initialValues={{ pin: '' }}
          validationSchema={schema}
          onSubmit={async (values, actions) => {
            try {
              const result = await api(`${ENDPOINT}/auth/confirm_whatsapp`, {
                method: 'POST',
                credentials: 'include',
                headers: {
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                  codigo: values.pin,
                  fone: state.fone,
                }),
              });

              const data = await result.json();

              if (data?.success === true) {
                setError(null);
                navigate('/cadastro/conferencia', { state: { ...state, hash: data.hash } });
              } else {
                actions.setFieldError('pin', 'Código incorreto');
              }
            } catch (error) {
              actions.setFieldError('pin', 'Não foi possível verificar o código digitado. Tente novamente mais tarde.');
            } finally {
              actions.setSubmitting(false);
            }
          }}
        >
          {(props) => {
            return (
              <Flex as={Form} flexDirection={'column'} gap={8}>
                <Heading fontSize="1em">
                  {`Olá ${firstName}, enviamos ${
                    successWhatsApp ? 'uma mensagem no WhatsApp' : successSMS ? 'um SMS' : null
                  } com seu código de segurança.`}
                  <Text fontSize={'xs'} mt={4}>
                    Insira-o abaixo e em seguida clique "Confirmar"
                  </Text>
                </Heading>

                <Field name="pin">
                  {({ field, form }) => {
                    return (
                      <FormControl isInvalid={form.errors.pin && form.touched.pin}>
                        <HStack spacing={2}>
                          <PinInput
                            size="md"
                            id="pin"
                            value={field.value}
                            onChange={(value) => form.setFieldValue('pin', value)}
                            onBlur={form.handleBlur}
                            otp
                            inputProps={{ textContentType: 'oneTimeCode', keyboardType: 'numeric', autoComplete: 'one-time-code' }}
                            isInvalid={form.errors.pin && form.touched.pin}
                          >
                            <PinInputField />
                            <PinInputField />
                            <PinInputField />
                            <PinInputField />
                            <PinInputField />
                            <PinInputField />
                            <PinInputField />
                            <PinInputField />
                          </PinInput>
                        </HStack>
                        <FormErrorMessage mt={4}>{form.errors.pin}</FormErrorMessage>
                      </FormControl>
                    );
                  }}
                </Field>

                <Button isFullWidth colorScheme="teal" type="submit" disabled={!props.dirty || !props.isValid} isLoading={props.isSubmitting}>
                  Confirmar
                </Button>
              </Flex>
            );
          }}
        </Formik>
        <BotaoEnviarWhatsApp
          sendWhatsApp={sendWhatsApp}
          successWhatsApp={successWhatsApp}
          successSMS={successSMS}
          sendSMS={sendSMS}
          setSuccessSMS={setSuccessSMS}
          setSuccessWhatsApp={setSuccessWhatsApp}
        />
      </Box>
    </>
  );
}

export default GeraCodigo;
